/**
 * Vertical Line Test generator.
 *
 * Render a relation as a set of plotted points and ask whether it represents a
 * function. A relation is a function exactly when no input (x) repeats - the
 * algebraic equivalent of "no vertical line hits the graph twice". The correct
 * answer is computed by checking for duplicate x-values, independent of how the
 * points were chosen.
 *
 * Families:
 *   W1: scattered points, is it a function?     -> boolean
 *   W2: points including a repeated x (not fn)   -> boolean (forced non-function)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";
import type { CoordinateModel } from "../graph/plot-model";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "vertical-line-test" as const;
const VERSION = "1.0.0";

type Family = "W1" | "W2";

interface Cfg {
  readonly range: number;
  readonly count: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { range: 5, count: 4 },
  standard: { range: 6, count: 5 },
  hard: { range: 7, count: 6 },
  challenge: { range: 8, count: 7 },
};

/** True if no x-value repeats (i.e. the relation is a function). */
function isFunction(points: Array<{ x: number; y: number }>): boolean {
  const xs = new Set<number>();
  for (const p of points) {
    if (xs.has(p.x)) return false;
    xs.add(p.x);
  }
  return true;
}

export class VerticalLineTestGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    const cfg = DIFF[ctx.difficulty];
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      // Coin flip whether to aim for a function or a non-function.
      const family: Family = rng.bool() ? "W1" : "W2";
      const wantFunction = family === "W1";

      const points: Array<{ x: number; y: number }> = [];
      if (wantFunction) {
        // Distinct x-values.
        const xsUsed = new Set<number>();
        while (points.length < cfg.count) {
          const x = rng.int(-cfg.range, cfg.range);
          if (xsUsed.has(x)) continue;
          xsUsed.add(x);
          points.push({ x, y: rng.int(-cfg.range, cfg.range) });
        }
      } else {
        // Force a repeated x with two different y's.
        const repeatX = rng.int(-cfg.range, cfg.range);
        const y1 = rng.int(-cfg.range, cfg.range);
        let y2 = rng.int(-cfg.range, cfg.range);
        if (y2 === y1) y2 = y1 + 1;
        points.push({ x: repeatX, y: y1 }, { x: repeatX, y: y2 });
        const xsUsed = new Set<number>([repeatX]);
        while (points.length < cfg.count) {
          const x = rng.int(-cfg.range, cfg.range);
          if (xsUsed.has(x) && points.length < cfg.count - 1) continue;
          xsUsed.add(x);
          points.push({ x, y: rng.int(-cfg.range, cfg.range) });
        }
      }

      // Independently compute the correct answer.
      const answerIsFunction = isFunction(points);
      // Ensure the built relation matches the intended family.
      if (answerIsFunction !== wantFunction) continue;

      const graph: CoordinateModel = {
        kind: "coordinate",
        xMin: -cfg.range - 1,
        xMax: cfg.range + 1,
        yMin: -cfg.range - 1,
        yMax: cfg.range + 1,
        points: points.map((p) => ({
          x: rat(p.x) as Rational,
          y: rat(p.y) as Rational,
          closed: true,
        })),
        segments: [],
      };

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: "Does this relation represent a function?",
          latex: "\\text{Does this relation represent a function?}",
        },
        answerType: "boolean",
        answer: { kind: "boolean", value: answerIsFunction },
        graph,
        steps: [
          {
            description: answerIsFunction
              ? "Each input (x) maps to exactly one output, so it is a function."
              : "One input (x) maps to two different outputs, so it is not a function.",
            latex: answerIsFunction ? "\\text{function}" : "\\text{not a function}",
          },
        ],
        hints: [
          { level: 1, text: "Check whether any x-value is used more than once." },
          { level: 2, text: "If a vertical line would cross two points, it fails the test." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "boolean" ? String(q.answer.value) : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const verticalLineTest = new VerticalLineTestGenerator();
